/**
 * Leasing Listings — boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/leasing-listings.schemas.ts`
 * imports and re-exports these to register the routes, and `apps/landlord/src/lib/leasing.api.ts`
 * derives its `ScreeningCriteria` / `UpsertScreeningCriteriaParams` types from the same objects.
 * See packages/api-types/README.md.
 *
 * Covers:
 *   GET  /listings/:id                       (screeningCriteria field)
 *   PUT  /listings/:id/screening-criteria
 *
 * Also covers every route returning a `ListingResponse` (as `listing`, or within `items`):
 *   GET    /landlord/api/listings
 *   POST   /landlord/api/listings
 *   GET    /landlord/api/listings/:id
 *   PATCH  /landlord/api/listings/:id
 *   POST   /landlord/api/listings/:id/activate
 *   POST   /landlord/api/listings/:id/pause
 *   POST   /landlord/api/listings/:id/close
 *   POST   /landlord/api/listings/:id/reopen
 *
 * Also covers (added in the #922 migration sweep):
 *   GET    /landlord/api/listings/jurisdiction-constraints
 *   GET    /landlord/api/listings/:id/availability
 *   POST   /landlord/api/listings/:id/availability/rules
 *   PATCH  /landlord/api/listings/:id/availability/rules/:ruleId
 *   POST   /landlord/api/listings/:id/availability/exceptions
 *
 * `ACCEPTED_LEASE_TERMS`, `ListingFeesSchema`, `ListingPetPolicySchema`, and
 * `ListingIncomeRestrictionsSchema` are the real (single source of truth) definitions —
 * they're also used server-side for write validation, so they live here and
 * `apps/api/src/services/leasing/listing.types.ts` imports + re-exports them rather than
 * redeclaring them (avoids drift between read and write validation).
 */
import { z } from 'zod';
import { JurisdictionCoverageSchema } from '../lease-generation/jurisdiction-coverage.js';
import { isoDateTimeString } from '../shared/date.js';
import { BillingPaymentErrorResponseSchema, DefaultPaymentMethodSchema } from '../billing/billing.js';
import { PublicListingActiveResponseSchema } from '../prospect/listing.schemas.js';
// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------
export const smokingPolicySchema = z.enum(['prohibited', 'outdoor_only', 'permitted']);
// Listing enums — mirrored as plain Zod enums (not cross-imported from the service layer).
export const ListingStatusSchema = z.enum(['draft', 'active', 'paused', 'closed']);
export const UtilityTypeSchema = z.enum(['water', 'sewer', 'trash', 'gas', 'electric', 'heat', 'cooling', 'internet', 'cable']);
export const FurnishedSchema = z.enum(['unfurnished', 'partial', 'furnished']);
export const PetTypeSchema = z.enum(['dogs', 'cats', 'birds', 'reptiles', 'fish', 'other']);
export const MaxPetSizeSchema = z.enum(['huge', 'large', 'medium', 'small', 'tiny', 'all']);
// ---------------------------------------------------------------------------
// Typed-JSON bag schemas (ADR-LS-008) — single source of truth for server-side write
// validation AND the listing response contract. Only canonical snake_case vocabulary —
// Zillow wire enums never appear here (ADR-LS-007).
// ---------------------------------------------------------------------------
/**
 * ListingFeesSchema — consolidates all move-in fees and monthly charges.
 * Amounts in cents. Single source of truth for the `fees` Json column.
 */
export const ListingFeesSchema = z.object({
    securityDepositCents: z.number().int().nonnegative().nullable().optional(),
    applicationFeeCents: z.number().int().nonnegative().nullable().optional(),
    petDepositCents: z.number().int().nonnegative().nullable().optional(),
    petRentMonthlyCents: z.number().int().nonnegative().nullable().optional(),
    firstMonthRequired: z.boolean().optional(),
    lastMonthRequired: z.boolean().optional(),
    administrationFeeCents: z.number().int().nonnegative().nullable().optional(),
    holdingDepositCents: z.number().int().nonnegative().nullable().optional(),
    parkingFeeMonthlyCents: z.number().int().nonnegative().nullable().optional(),
}).strict();
/**
 * ListingPetPolicySchema — per-listing pet policy.
 * Single source of truth for the `petPolicy` Json column.
 */
export const ListingPetPolicySchema = z.object({
    petsAllowed: z.boolean(),
    petTypesAllowed: z.array(PetTypeSchema).optional(),
    maxPetSize: MaxPetSizeSchema.nullable().optional(),
}).strict();
/**
 * ListingIncomeRestrictionsSchema — household-size income thresholds in cents/month.
 * Single source of truth for the `incomeRestrictions` Json column.
 */
export const ListingIncomeRestrictionsSchema = z.object({
    onePersonCents: z.number().int().nonnegative().nullable().optional(),
    twoPersonCents: z.number().int().nonnegative().nullable().optional(),
    threePersonCents: z.number().int().nonnegative().nullable().optional(),
    fourPersonCents: z.number().int().nonnegative().nullable().optional(),
    fivePersonCents: z.number().int().nonnegative().nullable().optional(),
    sixPersonCents: z.number().int().nonnegative().nullable().optional(),
    sevenPersonCents: z.number().int().nonnegative().nullable().optional(),
    eightPersonCents: z.number().int().nonnegative().nullable().optional(),
}).strict();
// ---------------------------------------------------------------------------
// Lease term canonical values
// ---------------------------------------------------------------------------
export const ACCEPTED_LEASE_TERMS = ['month_to_month', '6_months', '12_months', '24_months'];
// ---------------------------------------------------------------------------
// Listing response schema
// ---------------------------------------------------------------------------
const uuid = z.uuid();
const dateString = z.string(); // ISO 8601 string
/**
 * DC-51 — the unit's paid listing period (BR-92/BR-94). `null` on a listing with no current
 * period. `renewable` = inside the final renewal window with nothing queued.
 */
export const PaidPeriodSchema = z.object({
    endsAt: isoDateTimeString,
    renewalQueued: z.boolean(),
    renewable: z.boolean(),
});
/** "general" = "Yes, most days" (weekly hours); "limited" = "No, only at set times" (BR-107, BR-112). */
export const ShowingsAnswerSchema = z.enum(['general', 'limited']);
/**
 * listing-marketing-hub DC-5 — derived marketing state (BR-2): the listing's posted external-post
 * count and its activatedAt. Non-null on every list / detail response; null on mutation responses.
 */
export const ListingMarketingSchema = z.object({
    postedCount: z.number().int().nonnegative(),
    activatedAt: z.string().nullable(),
});
export const ListingResponseSchema = z.object({
    id: uuid,
    unitId: uuid,
    landlordScopeId: uuid,
    status: ListingStatusSchema,
    askingRentCents: z.number().int().nonnegative(),
    availableDate: dateString,
    acceptedLeaseTerms: z.array(z.enum(ACCEPTED_LEASE_TERMS)).min(1),
    smokingAllowed: z.boolean(),
    fees: ListingFeesSchema.nullable(),
    petPolicy: ListingPetPolicySchema.nullable(),
    incomeRestrictions: ListingIncomeRestrictionsSchema.nullable(),
    utilitiesIncluded: z.array(UtilityTypeSchema),
    furnished: FurnishedSchema,
    requireRentersInsurance: z.boolean(),
    screeningNotes: z.string().nullable(),
    pausedAutoReplyTemplate: z.string().nullable(),
    intakeAliasId: uuid.nullable(),
    intakeAddress: z.string().nullable(),
    activatedAt: dateString.nullable(),
    closedAt: dateString.nullable(),
    createdByUserId: uuid,
    createdAt: dateString,
    updatedAt: dateString,
    defaultShowingDurationMinutes: z.number().int().min(5).max(480),
    prospectBookingMode: z.enum(['proposal', 'direct']),
    /** The landlord's saved answer to "Are you generally available to show it?" (BR-107); null until answered. */
    showingsAnswer: ShowingsAnswerSchema.nullable(),
    // Iteration 3 (listing-syndication): public listing URL fields
    publicToken: z.string().nullable(),
    publicUrl: z.url().nullable(),
    // Iteration 6 (listing-syndication): listing-scoped contact channels (ListingContactChannel).
    // Declared once here so every route returning a ListingResponse (Create/Update/Activate/
    // Pause/Close/Reopen/List/Get) carries these fields consistently, instead of only
    // GetListingResponseSchema duplicating them as top-level fields.
    contactPhone: z.string().nullable(),
    contactName: z.string().nullable(),
    openLeadCount: z.number().int().nonnegative().nullable(),
    marketing: ListingMarketingSchema.nullable(),
    // Iteration 16 (saas-subscription) — DC-51
    paidPeriod: PaidPeriodSchema.nullable(),
    /** When the unit's most recent paid period ended (an expired listing's "Ended <date>"); null when one is current or none ever existed. */
    lastPeriodEndedAt: isoDateTimeString.nullable(),
    /** Server-computed: a current paid period OR the landlord's agent add-on is effectively active. */
    leasingAgentAvailable: z.boolean(),
});
// ---------------------------------------------------------------------------
// Screening criteria response
// ---------------------------------------------------------------------------
export const ScreeningCriteriaResponseSchema = z.object({
    listingId: z.uuid(),
    minIncomeMultiple: z.number().nullable(),
    maxOccupants: z.number().int().positive().nullable(),
    smokingPolicy: smokingPolicySchema,
    minCreditScore: z.number().int().positive().nullable(),
    additionalCriteria: z.string().nullable(),
    acceptsHousingVouchers: z.boolean(),
    publishScreeningCriteria: z.boolean(),
    criminalHistoryLookbackYears: z.number().int().nonnegative().nullable(),
    jurisdictionExtensions: z.record(z.string(), z.unknown()).nullable(),
    updatedAt: z.string(),
});
// ---------------------------------------------------------------------------
// Upsert screening criteria — request body
// ---------------------------------------------------------------------------
export const UpsertScreeningCriteriaBodySchema = z.object({
    minIncomeMultiple: z.number().positive().nullable().optional(),
    maxOccupants: z.number().int().positive().nullable().optional(),
    smokingPolicy: smokingPolicySchema.optional(),
    minCreditScore: z.number().int().positive().nullable().optional(),
    additionalCriteria: z.string().nullable().optional(),
    // BR-106: no defaults — an omitted field keeps its stored value. The service applies the
    // column defaults (both true) only when it creates the row.
    acceptsHousingVouchers: z.boolean().optional(),
    publishScreeningCriteria: z.boolean().optional(),
    criminalHistoryLookbackYears: z.number().int().nonnegative().nullable().optional(),
    jurisdictionExtensions: z.record(z.string(), z.unknown()).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Time/date primitives shared by the schemas below (mirrors the leasing-listings.schemas.ts
// route-local primitives — same convention as `uuid`/`dateString` above).
// ---------------------------------------------------------------------------
const timeString = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Must be HH:MM (24-hour)');
export const AvailabilityExceptionKindSchema = z.enum(['window', 'blackout']);
// ---------------------------------------------------------------------------
// Constraint warning — returned alongside create/update listing responses when a
// jurisdiction constraint was auto-corrected or is worth flagging.
// ---------------------------------------------------------------------------
export const ConstraintWarningSchema = z.object({
    code: z.string(),
    field: z.string(),
    message: z.string(),
    severity: z.literal('warning'),
});
// ---------------------------------------------------------------------------
// Jurisdiction constraints — GET /listings/jurisdiction-constraints
// ---------------------------------------------------------------------------
const ScreeningExtensionSchema = z.object({
    key: z.string(),
    label: z.string(),
    type: z.enum(['string', 'number', 'boolean', 'select']),
    options: z.array(z.string()).optional(),
    description: z.string().optional(),
    reason: z.string().optional(),
});
export const ListingConstraintsSchema = z.object({
    jurisdiction: z.object({
        chain: z.array(z.string()),
        displayName: z.string(),
        coverage: JurisdictionCoverageSchema,
    }),
    securityDeposit: z.object({
        maxMultiplier: z.number().nullable(),
        petDepositMaxMultiplier: z.number().nullable(),
        installmentRequiredAboveMultiplier: z.number().nullable(),
    }),
    moveInCost: z.object({
        maxFirstMonthMultiplier: z.number().nullable(),
        petDepositCountedInCeiling: z.boolean(),
    }),
    applicationFee: z.object({
        maxCents: z.number().int().nullable(),
        actualCostOnly: z.boolean(),
        refundIfUnused: z.boolean(),
        preApplicationDisclosureRequired: z.boolean(),
    }),
    screening: z.object({
        sourceOfIncomeProtected: z.boolean(),
        creditScoreAllowed: z.boolean(),
        incomeMultipleMax: z.number().nullable(),
        criminalHistoryLookbackMaxYears: z.number().nullable(),
        criminalHistoryAllowed: z.boolean(),
        disqualifyingFactorsMustBeIndividualized: z.boolean(),
        extensions: z.array(ScreeningExtensionSchema),
    }),
});
// ---------------------------------------------------------------------------
// Availability — rule and exception response schemas
// ---------------------------------------------------------------------------
export const AvailabilityRuleResponseSchema = z.object({
    id: uuid,
    listingId: uuid,
    weekdays: z.array(z.number().int().min(1).max(7)),
    startTime: timeString,
    endTime: timeString,
    effectiveFrom: dateString.nullable(),
    effectiveUntil: dateString.nullable(),
    notes: z.string().nullable(),
    createdByUserId: uuid,
    createdAt: dateString,
    updatedAt: dateString,
});
export const AvailabilityExceptionResponseSchema = z.object({
    id: uuid,
    listingId: uuid,
    date: dateString,
    kind: AvailabilityExceptionKindSchema,
    startTime: timeString.nullable(),
    endTime: timeString.nullable(),
    notes: z.string().nullable(),
    createdByUserId: uuid,
    createdAt: dateString,
});
// ---------------------------------------------------------------------------
// List listings — GET /landlord/api/listings
// ---------------------------------------------------------------------------
export const ListListingsResponseSchema = z.object({
    items: z.array(ListingResponseSchema),
    totalCount: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
// ---------------------------------------------------------------------------
// Create listing — POST /landlord/api/listings (request body)
// ---------------------------------------------------------------------------
export const CreateListingBodySchema = z.object({
    unitId: uuid,
    askingRentCents: z.number().int().nonnegative(),
    availableDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD'),
    acceptedLeaseTerms: z.array(z.enum(ACCEPTED_LEASE_TERMS)).min(1),
    smokingAllowed: z.boolean().optional(),
    fees: ListingFeesSchema.optional(),
    petPolicy: ListingPetPolicySchema.optional(),
    incomeRestrictions: ListingIncomeRestrictionsSchema.optional(),
    utilitiesIncluded: z.array(UtilityTypeSchema).default([]),
    furnished: z.enum(['unfurnished', 'partial', 'furnished']).default('unfurnished'),
    requireRentersInsurance: z.boolean().default(false),
    screeningNotes: z.string().nullable().optional(),
    pausedAutoReplyTemplate: z.string().nullable().optional(),
    defaultShowingDurationMinutes: z.number().int().min(5).max(480).optional(),
    prospectBookingMode: z.enum(['proposal', 'direct']).optional(),
    // BR-110: the Unit's marketing copy (description, amenities, photos, attributes) lives only on the Unit's
    // marketing profile. `.strict()` makes the Listing request reject those keys as unknown (400) instead of
    // silently stripping them.
}).strict();
// ---------------------------------------------------------------------------
// Get listing — GET /landlord/api/listings/:id
// ---------------------------------------------------------------------------
export const GetListingResponseSchema = z.object({
    listing: ListingResponseSchema,
    screeningCriteria: ScreeningCriteriaResponseSchema.nullable(),
    intakeAddress: z.string().nullable(),
    // Iteration 6 (listing-syndication): also on ListingResponseSchema (so every route
    // returning a listing carries them), duplicated here as top-level fields alongside the
    // existing intakeAddress convention — established, tested behavior for this endpoint.
    contactPhone: z.string().nullable(),
    contactName: z.string().nullable(),
    permissions: z.object({
        canManage: z.boolean(),
        canDecide: z.boolean(),
    }),
});
// ---------------------------------------------------------------------------
// Draft preview — GET /landlord/api/listings/:id/preview (BR-108, BR-109)
// ---------------------------------------------------------------------------
/**
 * The landlord's draft preview IS the public listing detail shape (status 'active', publicToken ''):
 * one schema, never redeclared, so preview and public page cannot drift (BR-108).
 */
export const ListingPreviewResponseSchema = PublicListingActiveResponseSchema;
// ---------------------------------------------------------------------------
// Update listing — PATCH /landlord/api/listings/:id (request body)
// ---------------------------------------------------------------------------
export const UpdateListingBodySchema = z.object({
    askingRentCents: z.number().int().nonnegative().optional(),
    availableDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD').optional(),
    acceptedLeaseTerms: z.array(z.enum(ACCEPTED_LEASE_TERMS)).min(1).optional(),
    smokingAllowed: z.boolean().optional(),
    fees: ListingFeesSchema.optional(),
    petPolicy: ListingPetPolicySchema.optional(),
    incomeRestrictions: ListingIncomeRestrictionsSchema.optional(),
    utilitiesIncluded: z.array(UtilityTypeSchema).optional(),
    furnished: z.enum(['unfurnished', 'partial', 'furnished']).optional(),
    requireRentersInsurance: z.boolean().optional(),
    screeningNotes: z.string().nullable().optional(),
    pausedAutoReplyTemplate: z.string().nullable().optional(),
    defaultShowingDurationMinutes: z.number().int().min(5).max(480).optional(),
    prospectBookingMode: z.enum(['proposal', 'direct']).optional(),
    showingsAnswer: ShowingsAnswerSchema.optional(),
    contactPhone: z.string().nullable().optional(),
    contactName: z.string().nullable().optional(),
    // BR-110: see CreateListingBodySchema, marketing keys are rejected, not stripped.
}).strict();
// ---------------------------------------------------------------------------
// Availability rules — create/update request bodies
// ---------------------------------------------------------------------------
export const CreateRuleBodySchema = z.object({
    weekdays: z.array(z.number().int().min(1).max(7)).nonempty(),
    startTime: timeString,
    endTime: timeString,
    effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    effectiveUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    notes: z.string().nullable().optional(),
});
export const UpdateRuleBodySchema = z.object({
    weekdays: z.array(z.number().int().min(1).max(7)).nonempty().optional(),
    startTime: timeString.optional(),
    endTime: timeString.optional(),
    effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    effectiveUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    notes: z.string().nullable().optional(),
});
// ---------------------------------------------------------------------------
// Availability exceptions — create request body
// ---------------------------------------------------------------------------
export const CreateExceptionBodySchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD'),
    kind: AvailabilityExceptionKindSchema,
    startTime: timeString.nullable().optional(),
    endTime: timeString.nullable().optional(),
    notes: z.string().nullable().optional(),
});
// ---------------------------------------------------------------------------
// Paid publish / renew — Iteration 16 (BR-88..BR-97)
// ---------------------------------------------------------------------------
/** DC-48 — GET /landlord/api/listings/:id/publish-quote */
export const PublishQuoteResponseSchema = z.object({
    /** False when the unit already has a current paid period (BR-92). */
    requiresFee: z.boolean(),
    /** Pre-tax, from the bundle snapshot; null = not offered (BR-89). */
    feeCents: z.number().int().nonnegative().nullable(),
    periodDays: z.number().int().positive().nullable(),
    currentPeriodEndsAt: isoDateTimeString.nullable(),
    defaultPaymentMethod: DefaultPaymentMethodSchema.nullable(),
    /**
     * True when this is the PINNED request of an earlier unresolved attempt under the caller's
     * idempotency key (BR-91): the fee and period are that attempt's, and the retry resumes it.
     */
    resumesEarlierAttempt: z.boolean(),
    /** The resumed attempt's period; null unless `resumesEarlierAttempt`. */
    pinnedPeriodStartsAt: isoDateTimeString.nullable(),
    pinnedPeriodEndsAt: isoDateTimeString.nullable(),
});
/** GET /landlord/api/listings/:id/publish-quote (query): the caller's persisted attempt key, if any. */
export const PublishQuoteQuerySchema = z.object({
    idempotencyKey: z.uuid().optional(),
});
/** POST /landlord/api/listings/:id/activate (request body). */
export const ActivateListingBodySchema = z.object({
    /** Must equal the server quote's feeCents when a fee is due (NFR-SECURITY-7). */
    consentToFeeCents: z.number().int().nonnegative().optional(),
    idempotencyKey: z.uuid(),
});
/** POST /landlord/api/listings/:id/renew (request body). */
export const RenewListingBodySchema = z.object({
    consentToFeeCents: z.number().int().nonnegative(),
    idempotencyKey: z.uuid(),
});
/**
 * Why the publish step failed after the listing fee was already paid (BR-110). The API
 * service imports this type rather than redeclaring it.
 */
export const PublishFailureCodeSchema = z.enum(['CONTACT_INFO_REQUIRED', 'LISTING_WRONG_STATUS', 'ACTIVATION_FAILED']);
export const PublishFailureSchema = z.object({
    code: PublishFailureCodeSchema,
    message: z.string(),
});
/** 200 body of POST /landlord/api/listings/:id/activate; `published:false` carries `publishFailure`. */
export const ActivateListingResponseSchema = z.object({
    listing: ListingResponseSchema,
    intakeAddress: z.string(),
    published: z.boolean(),
    publishFailure: PublishFailureSchema.optional(),
});
/** 200 body of POST /landlord/api/listings/:id/renew. */
export const RenewListingResponseSchema = z.object({
    listing: ListingResponseSchema,
    intakeAddress: z.string(),
});
export const ListingPaymentErrorCodeSchema = z.enum([
    'FEE_CONSENT_REQUIRED',
    'PAYMENT_METHOD_REQUIRED',
    'PAYMENT_DECLINED',
    'LISTING_FEE_NOT_OFFERED',
    'CHARGE_IN_PROGRESS',
    'LISTING_FEE_UNRECONCILED',
    'RENEWAL_NOT_AVAILABLE',
    'LISTING_PERIOD_REQUIRED',
]);
/**
 * 402 / 409 body of the paid activate and renew routes: the billing payment-error body with the
 * listing-specific `code` enum (the listing routes never carry `ineligibleReason`).
 */
export const ListingPaymentErrorResponseSchema = BillingPaymentErrorResponseSchema.omit({ ineligibleReason: true }).extend({
    code: ListingPaymentErrorCodeSchema,
});
//# sourceMappingURL=listings.js.map